// /src/pages/Proyectos.tsx
import React from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Progress,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { IProyecto } from '../types';
import { mockProyectos } from '../mocks/proyectos';

export const Proyectos: React.FC = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();

  // Filtrar proyectos según el rol del usuario
  const proyectosFiltrados = React.useMemo(() => {
    if (!usuario) return [];
    
    if (usuario.rol === 'Admin') {
      return mockProyectos;
    }
    
    if (usuario.rol === 'Cliente' && usuario.empresaId) {
      return mockProyectos.filter(p => p.empresaClienteId === usuario.empresaId);
    }
    
    // Consultores pueden ver todos los proyectos
    if (usuario.rol === 'Consultor') {
      return mockProyectos;
    }
    
    return [];
  }, [usuario]);

  const calcularProgreso = (proyecto: IProyecto): number => {
    const total = proyecto.lineamientos.length;
    if (total === 0) return 0;
    const cumple = proyecto.lineamientos.filter(l => l.estado === 'Cumple').length;
    return Math.round((cumple / total) * 100);
  };

  const getEstadoBadge = (estado: string) => {
    const colorScheme = {
      'Iniciado': 'blue',
      'En Auditoría': 'yellow',
      'Cerrado': 'green',
    }[estado] || 'gray';

    return <Badge colorScheme={colorScheme}>{estado}</Badge>;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="lg">Mis Proyectos</Heading>
      </Box>

      {proyectosFiltrados.length === 0 ? (
        <Box 
          bg="white" 
          borderRadius="lg" 
          boxShadow="sm" 
          p={8} 
          textAlign="center"
        >
          <Text color="gray.500" fontSize="lg">
            No tienes proyectos asignados
          </Text>
        </Box>
      ) : (
        <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Empresa</Th>
                <Th>Estándar</Th>
                <Th>Progreso</Th>
                <Th>Estado</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {proyectosFiltrados.map((proyecto) => {
                const progreso = calcularProgreso(proyecto);
                return (
                  <Tr key={proyecto.id}>
                    <Td fontWeight="medium">{proyecto.nombreEmpresa}</Td>
                    <Td>
                      <Text fontSize="sm" noOfLines={1}>
                        {proyecto.nombreEstandar}
                      </Text>
                    </Td>
                    <Td>
                      <Box>
                        <Progress value={progreso} colorScheme="blue" size="sm" mb={1} />
                        <Text fontSize="xs" color="gray.600">{progreso}%</Text>
                      </Box>
                    </Td>
                    <Td>{getEstadoBadge(proyecto.estadoGeneral)}</Td>
                    <Td>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => navigate(`/proyecto/${proyecto.id}`)}
                      >
                        Ver Detalles
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
